import { inject, injectable } from "inversify";
import { TYPES } from "@utils/inversifyJS/types";
import type { ISoftwareVersionsRepository, SoftwareVersion } from "@domain/repositories";

@injectable()
export class ListSoftwareVersionsService {
  @inject(TYPES.ISoftwareVersionsRepository)
  private _softwareVersionsRepository!: ISoftwareVersionsRepository;

  async getVersionsListAsync(): Promise<string[]> {
    const versions = await this._softwareVersionsRepository.ListAsync(true);
    return this.sortVersionStrings(versions.map((version) => version.version));
  }

  async getCandidateVersionsListAsync(): Promise<string[]> {
    const versions = await this.getSoftwareVersionsAsync();
    if (!Array.isArray(versions)) {
      return [];
    }
    return this.sortVersionStrings(this.normalizeVersionList(versions.map((version) => version.version)));
  }

  async getSoftwareVersionsAsync(enabled?: boolean): Promise<SoftwareVersion[]> {
    const versions = await this._softwareVersionsRepository.ListAsync(enabled);
    if (!Array.isArray(versions)) {
      return [];
    }
    return [...versions].sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true }));
  }

  async getDefaultSpectronautVersionAsync(candidateVersions?: string[]): Promise<string | undefined> {
    const candidates = new Set(this.normalizeVersionList(candidateVersions ?? await this.getCandidateVersionsListAsync()));
    const versions = await this.getSoftwareVersionsAsync(true);
    return versions.find((version) => version.isDefault && candidates.has(this.normalizeVersion(version.version)))?.version
      ?? versions.find((version) => candidates.has(this.normalizeVersion(version.version)))?.version;
  }

  async setDefaultSpectronautVersionAsync(version: string): Promise<void> {
    await this._softwareVersionsRepository.UpdateAsync(version, { isDefault: true, enabled: true });
  }

  async getSelectedSpectronautVersionsAsync(candidateVersions?: string[]): Promise<string[]> {
    const candidates = candidateVersions ?? await this.getCandidateVersionsListAsync();
    let versions: SoftwareVersion[];
    try {
      versions = await this.getSoftwareVersionsAsync(true);
    } catch {
      return candidates;
    }
    if (versions.length === 0) {
      return candidates;
    }

    const allowedVersions = new Set(this.normalizeVersionList(versions.map((version) => version.version)));
    return candidates.filter((version) => allowedVersions.has(version));
  }

  async setSelectedSpectronautVersionsAsync(candidateVersions: string[], selectedVersions: string[]): Promise<void> {
    const candidates = this.normalizeVersionList(candidateVersions);
    const selected = new Set(this.normalizeVersionList(selectedVersions));

    const versions = await this.getSoftwareVersionsAsync();
    const currentVersions = new Map(versions.map((version) => [this.normalizeVersion(version.version), version]));
    const versionsToUpdate = candidates.filter((version) => (currentVersions.get(version)?.enabled ?? false) !== selected.has(version));

    await Promise.all(versionsToUpdate.map((version) => this._softwareVersionsRepository.UpdateAsync(version, { enabled: selected.has(version) })));
  }

  private normalizeVersionList(versions: string[]): string[] {
    return [...new Set(versions.map((version) => this.normalizeVersion(version)).filter(Boolean))];
  }

  private normalizeVersion(version: string): string {
    return version.trim().replace(/^(?:SN|SM)/i, "");
  }

  private sortVersionStrings(versions: string[]): string[] {
    return [...versions].sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }
}
